import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

import {
  Normative,
  UpdateNormativeNewImageBody,
  UpdateNormativeRemoveImageBody,
} from "../../../../../http/border/types/normatives";
import { useNormativesApi } from "../../../../../http/border/useNormatives";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import { default as useImageUploader } from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Switch from "../../../../ui/Switch";
import Textarea from "../../../../ui/Textarea";
import TranslatableField from "../../../../ui/TranslatableField";

interface UpdateNormativeDetailsProps {
  name: string | null;
  description: string | null;
  descriptionIt: string | null;
  descriptionDe: string | null;
  descriptionEs: string | null;
  descriptionFr: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  reinforcedRailRequired: boolean;
}

interface NormativeDetailsProps {
  normative?: Normative;
  disabled?: boolean;
}

const NormativeDetails: FC<NormativeDetailsProps> = ({ normative, disabled }: NormativeDetailsProps) => {
  const i18n = useI18n();
  const api = useApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const normativesApi = useNormativesApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: normative?.image,
    isLoading: !normative || (!!normative && !normative?.id),
    disabled,
  });

  const defaultValues: UpdateNormativeDetailsProps = {
    name: normative?.name ?? null,
    description: normative?.description ?? null,
    descriptionIt: normative?.descriptionIt ?? null,
    descriptionDe: normative?.descriptionDe ?? null,
    descriptionEs: normative?.descriptionEs ?? null,
    descriptionFr: normative?.descriptionFr ?? null,
    order: normative?.order ?? 50,
    visibility: normative?.visibility ?? "hidden",
    reinforcedRailRequired: normative?.reinforcedRailRequired ?? false,
  };

  const submit = async (body: UpdateNormativeNewImageBody | UpdateNormativeRemoveImageBody) => {
    if (normative) {
      try {
        await normativesApi.updateAdminNormative(normative?.id, body);
        dispatch(showSuccessNotification(i18n.translation));
        onSave();
      } catch (err) {
        return dispatch(showErrorNotification(i18n, err));
      } finally {
        return setIsSubmitting(false);
      }
    }
  };

  const onSubmit = () => async (formData: UpdateNormativeDetailsProps) => {
    setIsSubmitting(true);

    const commonBody = {
      name: formData.name?.trim()!,
      description: formData.description?.trim()!,
      descriptionIt: formData.descriptionIt?.trim() ?? null,
      descriptionDe: formData.descriptionDe?.trim() ?? null,
      descriptionEs: formData.descriptionEs?.trim() ?? null,
      descriptionFr: formData.descriptionFr?.trim() ?? null,
      order: formData.order!,
      visibility: formData.visibility,
      reinforcedRailRequired: formData?.reinforcedRailRequired ?? false,
    };

    if (newImage && normative) {
      try {
        const signedUrl = await normativesApi.generateUploadImageUrl(normative.id, newImage.name, newImage.type);
        const res = await api.uploadFile(signedUrl, newImage);
        if (!res.ok) {
          dispatch(showErrorNotification(i18n, res));
          setIsSubmitting(false);
          return;
        }
        const body: UpdateNormativeNewImageBody = {
          ...commonBody,
          image: newImage.name,
        };
        await submit(body);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const body: UpdateNormativeRemoveImageBody = {
        ...commonBody,
        removeImage: removeImage,
      };
      await submit(body);
    }
  };

  return (
    <Form<UpdateNormativeDetailsProps>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || disabled}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, reset, watch }) => {
        const reinforcedRailRequired = !!watch("reinforcedRailRequired");
        const values = watch();

        useEffect(() => {
          reset(defaultValues);
        }, [normative]);

        useEffect(() => {
          register("descriptionIt");
          register("descriptionDe");
          register("descriptionEs");
          register("descriptionFr");
        }, [register]);

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={disabled}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.description} error={errors.description}>
              {({ labelId, isOptional, isInvalid }) => (
                <TranslatableField
                  field="description"
                  formValues={values}
                  setFormValue={setValue}
                  dialogTitle={i18n.translation.common.description}
                  dialogType="textarea"
                >
                  <Textarea
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={disabled}
                    {...register("description", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                </TranslatableField>
              )}
            </FormField>

            <FormField label={i18n.translation.common.reinforcedRailRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={reinforcedRailRequired}
                  disabled={disabled}
                  onChange={(checked) => setValue("reinforcedRailRequired", checked)}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={disabled}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={disabled}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default NormativeDetails;
